import { Link, type ErrorComponentProps } from '@tanstack/react-router';
import { Button, ThemeToggle } from '@lawbee/ui';
import type { ReactNode } from 'react';
import { useMeQuery } from '../lib/me';
import { Logo, PoweredBy, Wordmark } from './Logo';

export function RootErrorPage({ error, reset }: ErrorComponentProps) {
  return (
    <div className="flex min-h-dvh flex-col items-center justify-center gap-4 px-6 text-center">
      <Wordmark markClassName="size-10" />
      <h1 className="text-2xl font-semibold">Something went wrong</h1>
      <p className="max-w-md text-sm text-muted-foreground">{error instanceof Error ? error.message : 'An unexpected error occurred.'}</p>
      <div className="flex gap-2">
        <Button variant="outline" onClick={reset}>
          Try again
        </Button>
        <Button asChild>
          <Link to="/">Go home</Link>
        </Button>
      </div>
    </div>
  );
}

export function NotFoundPage() {
  return (
    <div className="flex min-h-dvh flex-col items-center justify-center gap-4 px-6 text-center">
      <Wordmark markClassName="size-10" />
      <h1 className="text-2xl font-semibold">Page not found</h1>
      <p className="max-w-md text-sm text-muted-foreground">The link may be out of date, or the page has moved.</p>
      <Button asChild>
        <Link to="/">Go home</Link>
      </Button>
    </div>
  );
}

/** Light marketing header + footer for the public site (landing, pricing). */
export function MarketingLayout({ children }: { children: ReactNode }) {
  const me = useMeQuery();
  return (
    <div className="flex min-h-dvh flex-col bg-background">
      <header className="sticky top-0 z-30 bg-background/85 backdrop-blur-xl">
        <div className="mx-auto flex h-16 max-w-6xl items-center gap-4 px-4 sm:px-8">
          <Link to="/" aria-label="LawBee home">
            <Wordmark />
          </Link>
          <nav aria-label="Site" className="ml-auto flex items-center gap-1 sm:gap-2">
            <Link to="/pricing" className="hidden rounded-lg px-3 py-2 text-sm font-medium text-muted-foreground hover:text-foreground sm:block">
              Pricing
            </Link>
            <ThemeToggle />
            {me.data ? (
              <Button asChild size="sm">
                <Link to="/dashboard">Open LawBee</Link>
              </Button>
            ) : (
              <>
                <Link to="/login" className="rounded-lg px-3 py-2 text-sm font-medium text-muted-foreground hover:text-foreground">
                  Sign in
                </Link>
                <Button asChild size="sm">
                  <Link to="/signup">Start free trial</Link>
                </Button>
              </>
            )}
          </nav>
        </div>
      </header>
      <main className="flex-1">{children}</main>
      <footer className="border-t">
        <div className="mx-auto flex max-w-6xl flex-col gap-2 px-4 py-8 text-sm text-muted-foreground sm:flex-row sm:items-center sm:justify-between sm:px-8">
          <span className="inline-flex items-center gap-2">
            <Logo className="size-5" label="" /> LawBee, a WRKRBZ app for Canadian law firms
          </span>
          <span>Client data is never used to train AI models.</span>
        </div>
      </footer>
    </div>
  );
}

/**
 * Minimal frame for client-facing public pages (intake, booking, pay). `embed` drops the chrome
 * for iframe embeds.
 */
export function PublicFrame({
  firmName,
  children,
  embed,
  footer,
  width = 'md',
}: {
  firmName?: string | null;
  children: ReactNode;
  embed?: boolean;
  footer?: ReactNode;
  width?: 'md' | 'lg';
}) {
  if (embed) return <div className="bg-background p-4 sm:p-6">{children}</div>;
  return (
    <div className="flex min-h-dvh flex-col bg-background">
      <header className="border-b bg-card">
        <div className={`mx-auto flex h-14 items-center gap-3 px-4 sm:px-6 ${width === 'lg' ? 'max-w-4xl' : 'max-w-2xl'}`}>
          <span className="truncate text-[15px] font-semibold">{firmName ?? ''}</span>
          <span className="ml-auto">
            <ThemeToggle />
          </span>
        </div>
      </header>
      <main className={`mx-auto w-full flex-1 px-4 py-8 sm:px-6 sm:py-10 ${width === 'lg' ? 'max-w-4xl' : 'max-w-2xl'}`}>{children}</main>
      <footer className="px-4 pb-8 text-center text-xs text-muted-foreground">
        {footer}
        <PoweredBy className="mt-2" />
      </footer>
    </div>
  );
}
